import React, { useContext } from "react";
import { Box, Tabs, Tab, Typography, Divider } from "@mui/material";
import ProgressBar from "../simple/ProgressBar";
import { useTheme } from "@mui/material/styles";
import DefaultBadge from "../simple/DefaultBadge";
import VerifiedIcon from "@mui/icons-material/Verified";
import EditIcon from "@mui/icons-material/Edit";
import PreviousResultsChart from "./PreviousResultsChart";
import ExerciseRecommendations from "../simple/ExerciseRecommendations";
import LessonResourcesDisplay from "../simple/LessonResourcesDisplay";
import { BrandContext } from "../../App";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

const ScenarioDetailsSidePanel = ({
  value,
  handleChange,
  scenarioData,
  simulationHistory,
}) => {
  const theme = useTheme();
  const { trainingProgram } = useContext(BrandContext);

  return !scenarioData.has_scoring_engine ? (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        maxHeight: "80vh",
        width: "60%",
        "@media (max-width:1080px)": {
          width: "100%",
        },
        backgroundColor: "transparent",
        boxShadow:
          "0px 12px 16px -4px rgba(154, 152, 191, 0.08), 0px 4px 6px -2px rgba(255, 255, 255, 0.03), 0px 2px 2px -1px rgba(255, 255, 255, 0.04)",
        border: `2px solid ${theme.palette.border.tertiary}`,
        borderRadius: theme.shape.radius,
        padding: theme.containers.paddingDesktop,
      }}
    >
      <Tabs
        value={value}
        onChange={handleChange}
        TabIndicatorProps={{ style: { display: "none" } }}
        textColor={theme.palette.text.primary}
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          padding: 0,
          mb: theme.customSpacing["sm"],
          gap: theme.customSpacing["2xl"],
          backgroundColor: "transparent",
          "& .MuiTab-root": {
            textTransform: "none",
            fontWeight: theme.typography.fontWeightMedium,
            fontSize: theme.typography.textSM.fontSize,
            color: theme.palette.text.primary,
            padding: `${theme.customSpacing.sm} ${theme.customSpacing.lg}`,
            borderRadius: "4px",
            minHeight: "unset",
            minWidth: "unset",
          },
          "& .Mui-selected": {
            backgroundColor: theme.palette.foreground.button_secondary,
            color: theme.palette.background.brand_alt,
            fontWeight: theme.typography.fontWeightRegular,
          },
        }}
      >
        <Tab label="Program Contents" />
        <Tab label="Resources" />
      </Tabs>

      <Divider
        sx={{
          backgroundColor: theme.palette.border.tertiary,
          height: 2,
          mb: theme.customSpacing["2xl"],
        }}
      />

      {value === 0 && trainingProgram?.chapters?.length > 0 && (
        <Box sx={{ mt: theme.customSpacing.xl, width: "100%" }}>
          {trainingProgram.chapters.map((chapter, index) => {
            const totalScenarios = chapter?.scenarios?.length || 0;
            const completedScenarios =
              chapter?.scenarios?.filter(
                (scenario) => scenario?.completed === true
              ).length || 0;

            const isCompleted =
              totalScenarios > 0 && completedScenarios === totalScenarios;

            // Calculate percentage
            const percentage =
              totalScenarios > 0
                ? Math.round((completedScenarios / totalScenarios) * 100)
                : 0;

            return (
              <Box
                key={chapter.id}
                sx={{
                  mb: theme.customSpacing["4xl"],
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <Typography
                  sx={{
                    fontWeight: theme.typography.fontWeightRegular,
                    fontSize: theme.typography.textSM.fontSize,
                    lineHeight: theme.typography.textSM.lineHeight,
                    color:
                      percentage === 0 || percentage === 100
                        ? theme.palette.text.primary
                        : "#962EFF",
                    maxWidth: "60%",
                    opacity: percentage === 0 ?  ".3" : '1',
                  }}
                >
                  {chapter.name}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    gap: "10px",
                    maxHeight: "30px",
                  }}
                >
                  {isCompleted ? (
                    <DefaultBadge
                      children={"Completed"}
                      startIcon={<VerifiedIcon />}
                      variant="pillSuccess"
                      size="small"
                    />
                  ) : (
                    <DefaultBadge
                      children={`${
                        percentage === 0 ? "Not Started" : "In Progress"
                      }`}
                      startIcon={
                        percentage === 0 ? <ErrorOutlineIcon /> : <EditIcon />
                      }
                      variant="pillTransparent"
                      size="small"
                      iconColor={
                        percentage === 0
                          ? "rgba(255,255,255,.3)"
                          : theme.palette.foreground.badge_icon
                      }
                      borderColor={
                        percentage === 0
                          ? "rgba(255,255,255,.3)"
                          : theme.palette.foreground.badge_icon
                      }
                      sx={{
                        color:
                          percentage === 0
                            ? "rgba(255,255,255,.3)"
                            : "rgb(168, 126, 255)",
                        fontSize: "11px",
                        gap: "3px",
                      }}
                    />
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {value === 0 && <ProgressBar trainingProgram={trainingProgram} />}

      {value === 1 && (
        <LessonResourcesDisplay
          resources={scenarioData?.resources}
          backgroundMode="dark"
        />
      )}
    </Box>
  ) : (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "80%",
        backgroundColor: "transparent",
        boxShadow:
          "0px 12px 16px -4px rgba(154, 152, 191, 0.08), 0px 4px 6px -2px rgba(255, 255, 255, 0.03), 0px 2px 2px -1px rgba(255, 255, 255, 0.04)",
        border: `2px solid ${theme.palette.border.tertiary}`,
        borderRadius: theme.shape.radius,
        padding: theme.containers.paddingDesktop,

        "@media (max-width:1300px)": {
          width: "100%!important",
        },
      }}
    >
      <Tabs
        value={value}
        onChange={handleChange}
        TabIndicatorProps={{ style: { display: "none" } }}
        textColor={theme.palette.text.primary}
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          padding: 0,
          mb: theme.customSpacing["sm"],
          gap: theme.customSpacing["2xl"],
          backgroundColor: "transparent",
          "& .MuiTab-root": {
            textTransform: "none",
            fontWeight: theme.typography.fontWeightMedium,
            fontSize: theme.typography.textSM.fontSize,
            color: theme.palette.text.primary,
            padding: `${theme.customSpacing.sm} ${theme.customSpacing.lg}`,
            borderRadius: "4px",
            minHeight: "unset",
            minWidth: "unset",
          },
          "& .Mui-selected": {
            backgroundColor: theme.palette.foreground.button_secondary,
            color: theme.palette.background.brand_alt,
            fontWeight: theme.typography.fontWeightRegular,
          },
        }}
      >
        <Tab label="Previous Results" />
        <Tab label="Program Contents" />
      </Tabs>

      <Divider
        sx={{
          backgroundColor: theme.palette.border.tertiary,
          height: 2,
          mb: theme.customSpacing["2xl"],
        }}
      />
      {value === 0 && trainingProgram?.chapters?.length > 0 && (
        // hides the tab
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: theme.customSpacing["3xl"],
          }}
        >
          <PreviousResultsChart scenarioData={scenarioData} />
          <ExerciseRecommendations
            scenarioData={scenarioData}
            simulationHistory={simulationHistory}
          />
        </Box>
      )}

      {value === 1 && trainingProgram?.chapters?.length > 0 && (
        <Box sx={{ mt: theme.customSpacing.xl, width: "100%" }}>
          {trainingProgram.chapters.map((chapter, index) => {
            const totalScenarios = chapter?.scenarios?.length || 0;
            const completedScenarios =
              chapter?.scenarios?.filter(
                (scenario) => scenario?.completed === true
              ).length || 0;

            const isCompleted =
              totalScenarios > 0 && completedScenarios === totalScenarios;

            // Calculate percentage
            const percentage =
              totalScenarios > 0
                ? Math.round((completedScenarios / totalScenarios) * 100)
                : 0;

            return (
              <Box
                key={chapter.id}
                sx={{
                  mb: theme.customSpacing["4xl"],
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                }}
              >
                <Typography
                  sx={{
                    fontWeight: theme.typography.fontWeightRegular,
                    fontSize: theme.typography.textSM.fontSize,
                    lineHeight: theme.typography.textSM.lineHeight,
                    color: theme.palette.text.primary,
                  }}
                >
                  {chapter.name}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    gap: "10px",
                    maxHeight: "30px",
                  }}
                >
                  {isCompleted ? (
                    <DefaultBadge
                      children={"Completed"}
                      startIcon={<VerifiedIcon />}
                      variant="pillSuccess"
                      size="small"
                    />
                  ) : (
                    <DefaultBadge
                      children={`${
                        percentage === 0 ? "Not Started" : "In Progress"
                      }`}
                      startIcon={
                        percentage === 0 ? <ErrorOutlineIcon /> : <EditIcon />
                      }
                      variant="pillTransparent"
                      size="small"
                      iconColor={
                        percentage === 0
                          ? "rgba(255,255,255,.3)"
                          : theme.palette.foreground.badge_icon
                      }
                      borderColor={
                        percentage === 0
                          ? "rgba(255,255,255,.3)"
                          : theme.palette.foreground.badge_icon
                      }
                      sx={{
                        color:
                          percentage === 0 ? "rgba(255,255,255,.3)" : "white",
                        fontSize: "11px",
                        gap: "3px",
                      }}
                    />
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {value === 1 && <ProgressBar trainingProgram={trainingProgram} />}
    </Box>
  );
};

export default ScenarioDetailsSidePanel;
